import React, { useState, useRef, useEffect } from 'react';
import BoardButton from '../BoardButton';
import { calculateGameScore, getNumHistoryFromSolution, getOperationHistoryFromSolution, progressCheck, convertMillisToSeconds } from '../../helper/helperFunctions';
import { main_background_color } from '../../constants';
import './styles/Game.css';
import { setCurrentGameData } from '../../services/state';
import { submitEventData } from '../../services/api';
import HowToPlay from './HowToPlay';

const Game = ({ basicUserData, currentBoardData, gameDone, setGameDone, setShowEndModal }) => {
    // console.log('currentBoardData:', currentBoardData);
    const { boardNum, targetNums, operators, solution_length } = currentBoardData;
    const limit = solution_length || 0;

    const scrollBoxRef = useRef(null); // Create a ref for the scrollable box

    const [operationHistory, setOperationsHistory] = useState([]);
    const [numHistory, setNumHistory] = useState([targetNums[0]]);
    const [numPermHistory, setNumPermHistory] = useState([]);
    const [actionHistory, setActionHistory] = useState([]);
    const [targetProgressNum, setTargetProgressNum] = useState(1);

    const startTimeRef = useRef(null);
    const [isTiming, setIsTiming] = useState(false);
    const [gameStarted, setGameStarted] = useState(false);
    const [showHowToPlay, setShowHowToPlay] = useState(true);
    const [elapsedMs, setElapsedMs] = useState(0);

    const reachedLimit = !gameDone && operationHistory.length >= limit;

    const functionsButtons = [
        { title: 'Undo', func: () => handleUndoClick() },
        { title: 'Clear', func: () => clearGame() },
    ];

    useEffect(() => {
        if (gameDone && Array.isArray(currentBoardData?.solution)) {
            setNumHistory(getNumHistoryFromSolution(targetNums, operators, currentBoardData.solution));
            setOperationsHistory(getOperationHistoryFromSolution(operators, currentBoardData.solution));
            setTargetProgressNum(currentBoardData.solution.length);
        } else if (gameDone) {
            setTargetProgressNum(targetNums.length);
        }
        // otherwise: do not auto-start on mount — wait for user to press Start
    }, [gameDone, boardNum, targetNums, operators, currentBoardData?.solution, currentBoardData?.solution_length]);

    // Scroll to the bottom whenever new content is added
    useEffect(() => {
        if (scrollBoxRef.current) {
            scrollBoxRef.current.scrollTo({
                top: scrollBoxRef.current.scrollHeight,
                behavior: 'smooth' // Smooth scrolling
            });
        }
    }, [operationHistory]);

    // Live timer updater
    useEffect(() => {
        if (!isTiming) return undefined;
        const id = setInterval(() => {
            if (startTimeRef.current) {
                const seconds = Math.floor((Date.now() - startTimeRef.current) / 1000);
                setElapsedMs(seconds * 1000);
            }
        }, 1000);
        return () => clearInterval(id);
    }, [isTiming]);

    // Function to handle the operator click
    const handleOperatorClick = (operator, givenNumHistory = numHistory, givenOperationHistory = operationHistory) => {

        // Do not allow moves before the game is started
        if (!gameStarted) return;

        // return if the operationHistory history length is greater than the limit
        if (operationHistory.length >= limit) {
            console.error("You have reached the limit");
            return;
        }

        // Parse the operator to get the number
        // i.e. if operator is '+1', then number is 1
        const operatorNumber = (operator.startsWith('+') || operator.startsWith('-'))
            ? parseInt(operator, 10)
            : parseInt(operator.slice(1), 10);

        const currentNumber = givenNumHistory[givenNumHistory.length - 1];
        let newNumber;

        // Perform the operation based on the operator
        // i.e. if operator is '+1', then add 1 to the current number
        if (operator.startsWith('+') || operator.startsWith('-')) {
            newNumber = currentNumber + operatorNumber;
        } else if (operator.startsWith('*')) {
            newNumber = currentNumber * operatorNumber;
        } else if (operator.startsWith('/')) {
            if (currentNumber !== 0) {
                newNumber = currentNumber / operatorNumber;
            } else {
                console.error("Cannot divide by zero");
                return;
            }
        }

        // Update the current number, number history, and operationHistory 
        const newNumHistory = [...givenNumHistory, newNumber];
        setNumHistory(newNumHistory);
        setOperationsHistory(() => [...givenOperationHistory, operator]);

        // Update the permament number history (for scoring)
        const newNumPermHistory = [...numPermHistory, operator];
        setNumPermHistory(() => newNumPermHistory);

        // Record action history for sharing (do not affect scoring)
        // store the operator itself (e.g. '+1', '*2')
        const newActionHistory = [...actionHistory, operator];
        setActionHistory(newActionHistory);
        console.log('actionHistory after op:', newActionHistory);

        const progress = progressCheck(targetNums, newNumHistory);
        setTargetProgressNum(progress);
        if (progress === targetNums.length) {
            playerFinished(newNumPermHistory, newActionHistory);
        }
    };


    const handleStart = () => {
        if (gameStarted) return;
        startTimeRef.current = Date.now();
        setIsTiming(true);
        setGameStarted(true);
        setShowHowToPlay(false);
        setElapsedMs(0);
        console.log('Timer started at', new Date(startTimeRef.current).toISOString());
    }



    const playerFinished = async (newNumPermHistory, finishedActionHistory) => {
        setGameDone(true);
        console.log('Final permanent num history:', newNumPermHistory);
        const timeTaken = getTimeTaken();
        // stop live timer
        setIsTiming(false);
        setElapsedMs(timeTaken);
        startTimeRef.current = null;
        console.log('Time taken (ms):', timeTaken, 'Formatted:', convertMillisToSeconds(timeTaken));
        const gameScore = getGameScore(timeTaken, newNumPermHistory);

        // Set the current game data (persist before showing modal)
        setCurrentGameData(boardNum, 'timeTaken', timeTaken)
        setCurrentGameData(boardNum, 'gameScore', gameScore)
        // persist action history and other details for sharing
        setCurrentGameData(boardNum, 'action_history', finishedActionHistory || actionHistory);
        // store permanent details so EndModal can build a shareable result
        setCurrentGameData(boardNum, 'num_history', newNumPermHistory)
        setCurrentGameData(boardNum, 'solution_length', solution_length)
        setCurrentGameData(boardNum, 'operators', operators)

        // Now it's safe to open the end modal (data persisted)
        setShowEndModal(true);

        const submitting_data = {
            user_browser_id: basicUserData.userId,
            num_history: newNumPermHistory,
            time_taken: timeTaken,
            game_score: gameScore,
            board_num: boardNum
        };

        // await submitUserData({user_browser_id: basicUserData.userId});
        // Submit the data to the server
        await submitEventData(submitting_data);

        console.log("Submitting data: ", submitting_data);
    }

    const clearGame = () => {
        if (operationHistory.length === 0) {
            return; // Do nothing if there is no history
        }
        // Record clear action for sharing and log it
        setActionHistory(prev => {
            const next = [...prev, 'C'];
            console.log('actionHistory after clear:', next);
            return next;
        });
        // Reset to initial state (no moves) but keep timer and started state
        const newNumHistory = numHistory.slice(0, 1);
        setNumHistory(newNumHistory);
        setOperationsHistory([]);
        setTargetProgressNum(progressCheck(targetNums, newNumHistory));
    }

    const handleUndoClick = () => {
        if (operationHistory.length === 0) return;
        // Record undo action for sharing and log it
        setActionHistory(prev => {
            const next = [...prev, 'U'];
            console.log('actionHistory after undo:', next);
            return next;
        });
        // Remove the last move
        const newNumHistory = numHistory.slice(0, numHistory.length - 1);
        setNumHistory(newNumHistory);
        setOperationsHistory(operationHistory.slice(0, operationHistory.length - 1));
        setTargetProgressNum(progressCheck(targetNums, newNumHistory));
    };

    // Get difference of time taken when started vs time ended
    const getTimeTaken = () => {
        if (!startTimeRef.current) return 0;
        return Date.now() - startTimeRef.current;
    };

    const getGameScore = (timeTaken, newNumPermHistory) => {
        const gameScore = calculateGameScore(timeTaken, newNumPermHistory.length - limit - 1);
        setCurrentGameData(boardNum, 'gameScore', gameScore);
        return gameScore;
    }

    // Respect persisted "show timer" preference
    const LS_KEY = 'combos_show_timer';
    const [showTimerPref, setShowTimerPref] = useState(true);

    useEffect(() => {
        try {
            const raw = localStorage.getItem(LS_KEY);
            if (raw !== null) setShowTimerPref(raw === 'true');
        } catch (e) { /* ignore */ }

        const handler = (e) => {
            try {
                const val = Boolean(e?.detail?.showTimer);
                setShowTimerPref(val);
            } catch (err) { /* ignore */ }
        };
        window.addEventListener('combos:showTimerChanged', handler);
        return () => window.removeEventListener('combos:showTimerChanged', handler);
    }, []);

    return (
        <div style={{ backgroundColor: main_background_color, display: 'flex', flexDirection: 'column', position: 'relative' }}>
            <div className='game-timer'>

                {(gameStarted || isTiming) && !gameDone && showTimerPref && (
                    <span style={{ backgroundColor: 'rgba(0, 0, 0, 0.45)', color: 'white', display: 'inline-block', height: '2.5vh', borderRadius: '4px', padding: '0.1em 0.5em' }}>
                        {Math.floor(elapsedMs / 1000)}s
                    </span>
                )}
            </div>


            <div className='target-numbers-container'>
                {targetNums.map((target, idx) => (
                    <div key={idx} className='target-numbers'>
                        {idx !== 0 && <p style={{ margin: '1em' }}>→</p>}
                        <div
                            className='target-number'
                            style={{
                                backgroundColor: targetProgressNum === idx ? 'var(--targetColorPresent)' : targetProgressNum > idx ? 'var(--targetColorPast)' : 'var(--targetColorFuture)'
                            }}>
                            <p className='target-number-number' style={{ visibility: (showHowToPlay && !gameDone) ? 'hidden' : 'visible' }}>{target}</p>
                        </div>
                    </div>
                ))}
            </div>

            <div className="number-container">
                <div className="number-box" ref={scrollBoxRef}>
                    {numHistory.map((elm, idx) => (
                        <div key={idx}>
                            <p className='number-operation'
                                style={{
                                    color: 'var(--numberColorOperation)'
                                }}>{operationHistory[idx - 1]}</p>
                            <p className={`${idx === numHistory.length - 1 ? 'number-current-number' : 'number-past-number'} ${idx === numHistory.length - 1 && reachedLimit ? 'limit-reached-number' : ''}`}>{(showHowToPlay && !gameDone) ? 'X' : elm}</p>
                        </div>

                    ))}
                </div>
            </div>

            <p className={`limit-text ${reachedLimit ? 'limit-reached' : ''}`}>{operationHistory.length} / {(showHowToPlay && !gameDone) ? 'X' : limit}</p>

            <div className='button-container'>
                {!gameDone ?
                    <>
                        {!gameStarted ? (
                            showHowToPlay ? (
                                <HowToPlay onStart={handleStart} onClose={() => setShowHowToPlay(false)} />
                            ) : (
                                <div className='start-controls'>
                                    <BoardButton className='start-button' onClick={handleStart} label={'Start'}>Start</BoardButton>
                                    <BoardButton className='start-button' onClick={() => setShowHowToPlay(true)} label={'How to Play'}>How to Play</BoardButton>
                                </div>
                            )
                        ) : (
                            <>
                                <div className='operation-buttons'>
                                    {operators.map((operator, index) => (
                                        <BoardButton className='operation-button' key={index} onClick={() => handleOperatorClick(operator)} label={(showHowToPlay && !gameDone) ? '' : operator} disabled={(showHowToPlay && !gameDone)}>{(showHowToPlay && !gameDone) ? '' : operator}</BoardButton>
                                    ))}
                                </div>

                                <div className='function-buttons'>
                                    {functionsButtons.map((button, index) => (
                                        <BoardButton className='function-button' key={index} onClick={button.func} label={button.title}>{button.title}</BoardButton>
                                    ))}
                                </div>
                            </>
                        )}
                    </>
                    :
                    <>
                        <BoardButton className='operation-button' onClick={() => setShowEndModal(true)} label={'Results'} />
                    </>
                }
            </div>

        </div>
    );
};

export default Game;
